/**
 * @fileoverview Hook data layer for loading customer custom fields in CustomerDetailMC
 * @module useCustomerDetailData
 * @category Data Layer
 * @since 1.0.0
 * @version 2.1.0
 */

/**
 * @typedef {Object} CustomerField
 * @description Customer custom field for CustomerDetailMC
 * @property {string|number} id - Unique ID of the field
 * @property {string} name - Field name
 * @property {string} value - Field value
 * @property {boolean} [isCustom] - Indicates if it is a custom field
 */

/**
 * @typedef {Object} UseCustomerDetailDataReturn
 * @description Object returned by the useCustomerDetailData hook
 * @property {CustomerField[]} customerFields - Customer standard fields
 * @property {CustomerField[]} customFields - Customer custom fields
 * @property {function} loadCustomerFieldData - Function to reload the fields
 */

import {
  useState,
  useEffect,
  useCallback,
  useMemo
} from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import customersModelMc from '../../../models/CustomersModelMc';
import { LOAD_DATA_FAILURE } from '../../../../actions/inbox/conversationActions';
import { showInboxApiErrorToast } from '../utils/showInboxApiErrorToast';

/**
 * Hook data layer for loading customer custom and standard fields in CustomerDetailMC.
 *
 * Implements the Data Layer pattern with strict separation of responsibilities:
 * - Only manages data loading and associated states
 * - No manages UI logic or business validations
 * - Optimized with useCallback, useMemo and useRef to avoid unnecessary renders
 *
 * Operation flow:
 * 1. Initializes states for customer fields
 * 2. Provides function to load data from the API
 * 3. Handles errors and loading states
 *
 * @function useCustomerDetailData
 * @category Hook - Data Layer
 * @param {string|number} conversationId - ID of the conversation associated with the customer
 * @returns {UseCustomerDetailDataReturn} Object with fields and function to reload
 * @see customersModelMc.getCustomerFields
 * @since 1.0.0
 * @version 2.1.0 - Performance optimization and documentation
 */
function useCustomerDetailData(conversationId) {
  const { t } = useTranslation('InboxMC');
  const dispatch = useDispatch();

  // Estados de datos puros
  const [customerFields, setCustomerFields] = useState([]);
  const [customFields, setCustomFields] = useState([]);

  /**
   * Function to load customer fields from the API.
   *
   * Performs the query to customersModelMc and updates the local states.
   * Handles errors and updates the global state in case of failure.
   *
   * @memberof useCustomerDetailData
   * @async
   * @function loadCustomerFieldData
   * @throws {Error} If an error occurs in the field query
   * @example
   * loadCustomerFieldData();
   */
  const loadCustomerFieldData = useCallback(async () => {
    if (!conversationId) return;

    try {
      const data = await customersModelMc.getCustomerFields(conversationId);
      const newCustomerFields = data.customer_fields || [];
      const newCustomFields = data.custom_fields || [];
      setCustomerFields(newCustomerFields);
      setCustomFields(newCustomFields);
    } catch (err) {
      const errorMessage = showInboxApiErrorToast(
        err,
        t('customerDetails.error')
      );
      dispatch({
        type: LOAD_DATA_FAILURE,
        error: errorMessage
      });
    }
  }, [conversationId, dispatch, t]);

  // Effect to load data when the conversationId changes
  useEffect(() => {
    setCustomerFields([]);
    setCustomFields([]);
  }, [conversationId]);

  return useMemo(() => ({
    customerFields,
    customFields,
    loadCustomerFieldData
  }), [
    customerFields,
    customFields,
    loadCustomerFieldData
  ]);
}

export default useCustomerDetailData;
